const GIORNI_LABELS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const GIORNI_COMPLETI = [
  "Lunedì",
  "Martedì",
  "Mercoledì",
  "Giovedì",
  "Venerdì",
  "Sabato",
  "Domenica",
];

export function formatGiorniSettimana(giorni) {
  if (!giorni || giorni.length === 0) return "Tutti i giorni";
  return [...giorni]
    .sort((a, b) => a - b)
    .map((g) => GIORNI_LABELS[g] || String(g))
    .join(", ");
}

export function formatOrari(orari) {
  if (!orari || orari.length === 0) return "—";
  return [...orari].sort().join(", ");
}

/** Primo orario del giorno (HH:MM), per ordinamento elenco terapie. */
export function earliestOrarioSortKey(orari) {
  if (!orari || orari.length === 0) return null;
  const clean = orari.map((o) => String(o ?? "").trim()).filter(Boolean);
  if (clean.length === 0) return null;
  return [...clean].sort()[0];
}

/** Confronto terapie: orario (crescente), poi nome alfabetico. Senza orario in fondo. */
export function compareTerapieByOrarioNome(a, b) {
  const orarioA = earliestOrarioSortKey(a?.orari);
  const orarioB = earliestOrarioSortKey(b?.orari);

  if (orarioA == null && orarioB == null) {
    return (a?.nome || "").localeCompare(b?.nome || "", "it");
  }
  if (orarioA == null) return 1;
  if (orarioB == null) return -1;

  const timeCmp = orarioA.localeCompare(orarioB);
  if (timeCmp !== 0) return timeCmp;

  return (a?.nome || "").localeCompare(b?.nome || "", "it");
}

export function sortTerapieByOrarioNome(terapie) {
  return [...(terapie || [])].sort(compareTerapieByOrarioNome);
}

function isOgniGiorno(giorni) {
  if (!giorni || giorni.length === 0) return true;
  return giorni.length >= 7;
}

function formatFrequenzaGiorni(giorni) {
  if (isOgniGiorno(giorni)) return "Ogni giorno";
  return [...giorni]
    .sort((a, b) => a - b)
    .map((g) => GIORNI_COMPLETI[g] || GIORNI_LABELS[g] || String(g))
    .join(", ");
}

function isAlBisogno(note) {
  if (!note) return false;
  return /al\s+bisogno|a\s+bisogno|\bprn\b|se\s+necessario/i.test(String(note));
}

/** Testo posologia per stampa: dosaggio, frequenza, modalità e note. */
export function formatPosologiaSomministrazione(te) {
  const parts = [];
  const dosaggio = String(te?.dosaggio ?? "").trim();
  if (dosaggio) parts.push(dosaggio);
  parts.push(formatFrequenzaGiorni(te?.giorni_settimana));
  parts.push(isAlBisogno(te?.note) ? "Al bisogno" : "All'orario indicato");

  let posologia = parts.join(" ");
  const note = String(te?.note ?? "").trim();
  if (note && !isAlBisogno(note)) {
    posologia = `${posologia}. ${note}`;
  }
  return posologia;
}

/**
 * Espande le terapie in righe stampabili (una per orario), ordinate per orario e nome.
 * Ogni riga: { dataInizio, dataFine, farmaco, posologia, orario }
 */
export function expandTerapieForPrintRows(terapie) {
  const rows = [];

  for (const te of terapie || []) {
    if (!te || typeof te !== "object") continue;
    const farmaco = String(te.nome ?? "").trim();
    if (!farmaco) continue;

    const dataInizio = te.data_inizio ? formatDataIt(te.data_inizio) : "";
    const dataFine = te.data_fine ? formatDataIt(te.data_fine) : "";
    const posologia = formatPosologiaSomministrazione(te);
    const orari = (te.orari || []).map((o) => String(o ?? "").trim()).filter(Boolean);

    if (orari.length === 0) {
      rows.push({
        dataInizio,
        dataFine,
        farmaco,
        posologia,
        orario: "",
        sortOrario: "99:99",
        sortNome: farmaco.toLowerCase(),
      });
      continue;
    }

    for (const orario of [...orari].sort()) {
      rows.push({
        dataInizio,
        dataFine,
        farmaco,
        posologia,
        orario,
        sortOrario: orario,
        sortNome: farmaco.toLowerCase(),
      });
    }
  }

  rows.sort((a, b) => {
    const timeCmp = a.sortOrario.localeCompare(b.sortOrario);
    if (timeCmp !== 0) return timeCmp;
    return a.sortNome.localeCompare(b.sortNome, "it");
  });

  return rows;
}

export function formatDataIt(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(`${dateStr}T12:00:00`);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("it-IT");
  } catch {
    return dateStr;
  }
}

export function formatScheduledLocal(isoLocalOrUtc) {
  if (!isoLocalOrUtc) return "—";
  try {
    const d = new Date(isoLocalOrUtc);
    if (Number.isNaN(d.getTime())) return isoLocalOrUtc;
    return d.toLocaleString("it-IT", {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return isoLocalOrUtc;
  }
}

export function summarizeTerapiaExtra(te) {
  if (!te) return "";
  const parts = [
    formatGiorniSettimana(te.giorni_settimana),
    formatOrari(te.orari),
  ];
  if (te.data_fine) {
    parts.push(`fino al ${formatDataIt(te.data_fine)}`);
  } else if (te.data_inizio) {
    parts.push(`dal ${formatDataIt(te.data_inizio)}`);
  }
  return parts.filter(Boolean).join(" · ");
}

export { GIORNI_LABELS };
